/**
 * PriceFactors - Fatores que Influenciam o Preço da Borracha
 * 
 * Baseado no Tripé:
 * JUSTICA - Informações de fontes oficiais
 * SEGURANCA - Dados verificáveis
 * SIMPLICIDADE - Explicação clara para o produtor
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ChevronDown, ChevronUp, Globe, DollarSign, Ship, Calendar, Handshake, Coins, Landmark, AlertTriangle, TrendingDown, TrendingUp } from 'lucide-react';

const iconMap = {
  globe: Globe,
  dollar: DollarSign,
  ship: Ship,
  calendar: Calendar,
  handshake: Handshake,
  coins: Coins,
  landmark: Landmark,
};

const PriceFactors = () => {
  const [factors, setFactors] = useState([]);
  const [expandedFactor, setExpandedFactor] = useState(null);
  const [formulaData, setFormulaData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const [factorsRes, formulaRes] = await Promise.all([
        fetch(`${API_URL}/api/market/price-factors`),
        fetch(`${API_URL}/api/market/formula-explanation`)
      ]);
      
      if (factorsRes.ok) {
        const data = await factorsRes.json();
        setFactors(data.factors);
      }
      if (formulaRes.ok) {
        const data = await formulaRes.json();
        setFormulaData(data);
      }
    } catch (error) {
      console.error('Erro ao carregar fatores:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  const paradox = formulaData?.paradox;

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-2 border-green-500 bg-gradient-to-r from-green-50 to-emerald-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-green-800 flex items-center gap-2" data-testid="price-factors-title">
            <Globe className="w-6 h-6" />
            O Que Forma o Preço da Borracha
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700">
            O preço que o produtor recebe é resultado da combinação de vários fatores. Entender cada um ajuda a negociar melhor e tomar decisões informadas.
          </p>
        </CardContent>
      </Card>

      {/* Cadeia de Formacao do Preco */}
      {formulaData?.price_formation_chain && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-blue-600" />
              Cadeia de Formação do Preço
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Do preço internacional até o bolso do produtor:
            </p>
            <div className="space-y-1">
              {formulaData.price_formation_chain.map((step, idx) => (
                <div key={idx} className="flex items-start sm:items-center gap-3 p-3 rounded-lg bg-gray-50 border" data-testid={`chain-step-${idx}`}>
                  <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {step.step}
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-800 text-sm">{step.name}</div>
                      <div className="text-xs text-gray-500">{step.description}</div>
                    </div>
                    <div className="text-sm font-mono text-green-700 font-medium sm:whitespace-nowrap sm:text-right flex-shrink-0">
                      {step.value_example}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* O Paradoxo */}
      {paradox && (
        <Card className="border-2 border-amber-400 bg-amber-50" data-testid="paradox-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {paradox.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">{paradox.explanation}</p>
            
            {paradox.real_example && (
              <div className="bg-white rounded-lg p-4 border border-amber-300">
                <h4 className="font-bold text-gray-800 mb-3">Exemplo Real:</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">{paradox.real_example.period_before}</div>
                    <div className="text-sm">GEB-10: <strong>R$ {paradox.real_example.geb10_before}</strong></div>
                    <div className="text-sm">Coagulo: <strong>R$ {paradox.real_example.coagulo_before}</strong></div>
                    <div className="text-xs text-gray-500 mt-1">Participacao: {paradox.real_example.participation_before}%</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">{paradox.real_example.period_after}</div>
                    <div className="text-sm">GEB-10: <strong className="text-green-700">R$ {paradox.real_example.geb10_after}</strong> <TrendingUp className="w-3 h-3 inline text-green-600" /></div>
                    <div className="text-sm">Coagulo: <strong className="text-red-700">R$ {paradox.real_example.coagulo_after}</strong> <TrendingDown className="w-3 h-3 inline text-red-600" /></div>
                    <div className="text-xs text-gray-500 mt-1">Participacao: {paradox.real_example.participation_after}%</div>
                  </div>
                </div>
                <p className="text-sm text-amber-800 mt-3 font-medium text-center">
                  {paradox.real_example.conclusion}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Fatores - Expandiveis */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-gray-800">
            7 Fatores que Determinam Seu Preço
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {factors.map((factor) => {
            const IconComponent = iconMap[factor.icon] || Globe;
            const isExpanded = expandedFactor === factor.id;
            
            return (
              <div 
                key={factor.id}
                className="border rounded-lg overflow-hidden"
                data-testid={`factor-${factor.id}`}
              >
                <button
                  onClick={() => setExpandedFactor(isExpanded ? null : factor.id)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-5 h-5 text-green-700" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-800">{factor.title}</h4>
                    <p className="text-sm text-gray-500 line-clamp-1">{factor.description}</p>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                
                {isExpanded && (
                  <div className="px-4 pb-4 border-t bg-gray-50">
                    <p className="text-gray-700 mt-3 mb-3">{factor.description}</p>
                    
                    <div className="space-y-2 mb-3">
                      {factor.details?.map((detail, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <span className="text-green-500 mt-0.5 flex-shrink-0">&#10003;</span>
                          <span className="text-gray-600">{detail}</span>
                        </div>
                      ))}
                    </div>
                    
                    {factor.impact && (
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-sm text-amber-800">
                          <strong>Impacto no produtor:</strong> {factor.impact}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* COE vs COT */}
      {formulaData?.cost_analysis && (
        <Card className="border-2 border-red-300" data-testid="cost-analysis-section">
          <CardHeader className="pb-2 bg-red-50">
            <CardTitle className="text-red-800 flex items-center gap-2">
              <Coins className="w-5 h-5" />
              Custos de Produção: COE vs COT
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">{formulaData.cost_analysis.coe.label}</h4>
                <div className="text-3xl font-bold text-orange-700 my-2">R$ {formulaData.cost_analysis.coe.value.toFixed(2)}/kg</div>
                <p className="text-sm text-gray-600">{formulaData.cost_analysis.coe.description}</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">{formulaData.cost_analysis.cot.label}</h4>
                <div className="text-3xl font-bold text-red-700 my-2">R$ {formulaData.cost_analysis.cot.value.toFixed(2)}/kg</div>
                <p className="text-sm text-gray-600">{formulaData.cost_analysis.cot.description}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="text-green-600 font-bold text-lg">&#10003;</span>
                <span className="text-sm text-gray-700">{formulaData.cost_analysis.interpretation.above_cot}</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">{formulaData.cost_analysis.interpretation.between}</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <span className="text-red-600 font-bold text-lg">&#10007;</span>
                <span className="text-sm text-gray-700">{formulaData.cost_analysis.interpretation.below_coe}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fontes */}
      {formulaData?.sources && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-gray-700">Fontes Oficiais</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {formulaData.sources.map((source, idx) => (
                <a
                  key={idx}
                  href={`https://${source.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 border transition-colors"
                >
                  <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{source.name}</div>
                    <div className="text-xs text-gray-500">{source.data}</div>
                  </div>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PriceFactors;
